import { FC, useEffect, useMemo } from 'react';
import { cn } from '@shadcn/ui/lib/utils';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@shadcn/ui/components/ui/tooltip';
import { ContactSuggestionReason } from '@zaplify/campaigns';
import { useSuggestionsFeed } from '../hooks/use-suggestions-feed';
import { useFlags } from 'launchdarkly-react-client-sdk';

export const NetworkDistanceFilter1: FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const { suggestionsMatchingSelectedPlaybook: contactSuggestions } = useSuggestionsFeed();

    const numberOfSuggestionsInsideNetwork = useMemo(() => {
        return contactSuggestions.filter(
            (suggestion) => suggestion.reason !== ContactSuggestionReason.SIMILAR_TO_OTHER_ACCOUNTS.toString()
        ).length;
    }, [contactSuggestions]);
    const hasSuggestionsInsideNetwork = useMemo(() => {
        return numberOfSuggestionsInsideNetwork > 0;
    }, [numberOfSuggestionsInsideNetwork]);

    const numberOfSuggestionsOutsideNetwork = useMemo(() => {
        return contactSuggestions.filter(
            (suggestion) => suggestion.reason === ContactSuggestionReason.SIMILAR_TO_OTHER_ACCOUNTS.toString()
        ).length;
    }, [contactSuggestions]);
    const hasSuggestionsOutsideNetwork = useMemo(() => {
        return numberOfSuggestionsOutsideNetwork > 0;
    }, [numberOfSuggestionsOutsideNetwork]);

    const isInsideNetworkSelected = searchParams.get('insideNetwork') !== 'false';

    useEffect(() => {
        if (!location.search.includes('insideNetwork')) {
            setSearchParams((prevParams) => {
                const newParams = new URLSearchParams(prevParams);
                if (hasSuggestionsInsideNetwork) {
                    newParams.set('insideNetwork', 'true');
                } else if (hasSuggestionsOutsideNetwork) {
                    newParams.set('insideNetwork', 'false');
                }
                return newParams;
            });
        }
    }, [hasSuggestionsInsideNetwork, hasSuggestionsOutsideNetwork, setSearchParams, location.pathname]);

    // Add this check to force 'false' when there are no inside network suggestions
    useEffect(() => {
        if (!hasSuggestionsInsideNetwork && hasSuggestionsOutsideNetwork) {
            setSearchParams((prevParams) => {
                const newParams = new URLSearchParams(prevParams);
                newParams.set('insideNetwork', 'false');
                return newParams;
            });
        }
        if (!hasSuggestionsOutsideNetwork && hasSuggestionsInsideNetwork) {
            setSearchParams((prevParams) => {
                const newParams = new URLSearchParams(prevParams);
                newParams.set('insideNetwork', 'true');
                return newParams;
            });
        }
    }, [hasSuggestionsInsideNetwork, hasSuggestionsOutsideNetwork, setSearchParams]);

    if (!hasSuggestionsInsideNetwork && !hasSuggestionsOutsideNetwork) return null;

    const handleInsideClick = () => {
        if (!hasSuggestionsInsideNetwork) return;
        setSearchParams((prevParams) => {
            prevParams.set('insideNetwork', 'true');
            return prevParams;
        });
    };

    const handleOutsideClick = () => {
        if (!hasSuggestionsOutsideNetwork) return;
        setSearchParams((prevParams) => {
            prevParams.set('insideNetwork', 'false');
            return prevParams;
        });
    };

    return (
        <TooltipProvider>
            <div className="flex items-center gap-1">
                <Tooltip>
                    <TooltipTrigger asChild>
                        <button
                            onClick={handleInsideClick}
                            disabled={!hasSuggestionsInsideNetwork}
                            className={cn(
                                'flex items-center gap-2 px-3 py-2 rounded text-sm transition-all shadow-none',
                                isInsideNetworkSelected && hasSuggestionsInsideNetwork
                                    ? 'text-foreground font-semibold'
                                    : 'text-foreground-tertiary font-medium hover:text-foreground',
                                !hasSuggestionsInsideNetwork && 'opacity-50 cursor-not-allowed'
                            )}
                        >
                            <span>Inside network</span>
                            <span
                                className={cn(
                                    'min-w-[20px] h-5 px-1.5 rounded-full text-xs font-medium flex items-center justify-center',
                                    'bg-muted text-muted-foreground'
                                )}
                            >
                                {numberOfSuggestionsInsideNetwork}
                            </span>
                        </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">Contacts who are already in your LinkedIn network</TooltipContent>
                </Tooltip>

                <Tooltip>
                    <TooltipTrigger asChild>
                        <button
                            onClick={handleOutsideClick}
                            disabled={!hasSuggestionsOutsideNetwork}
                            className={cn(
                                'flex items-center gap-2 px-3 py-2 rounded text-sm transition-all shadow-none',
                                !isInsideNetworkSelected && hasSuggestionsOutsideNetwork
                                    ? 'text-foreground font-semibold'
                                    : 'text-foreground-tertiary font-medium hover:text-foreground',
                                !hasSuggestionsOutsideNetwork && 'opacity-50 cursor-not-allowed'
                            )}
                        >
                            <span>Outside network</span>
                            <span
                                className={cn(
                                    'min-w-[20px] h-5 px-1.5 rounded-full text-xs font-medium flex items-center justify-center',
                                    'bg-muted text-muted-foreground'
                                )}
                            >
                                {numberOfSuggestionsOutsideNetwork}
                            </span>
                        </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                        Contacts who are not yet in your LinkedIn network but may be relevant
                    </TooltipContent>
                </Tooltip>
            </div>
        </TooltipProvider>
    );
};

export const NetworkDistanceFilter2: FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const { suggestionsMatchingSelectedPlaybook: contactSuggestions } = useSuggestionsFeed();
    const { shadcnSearchEnabled } = useFlags();

    const numberOfSuggestionsInsideNetwork = useMemo(() => {
        return contactSuggestions.filter(
            (suggestion) => suggestion.reason !== ContactSuggestionReason.SIMILAR_TO_OTHER_ACCOUNTS.toString()
        ).length;
    }, [contactSuggestions]);
    const hasSuggestionsInsideNetwork = useMemo(() => {
        return numberOfSuggestionsInsideNetwork > 0;
    }, [numberOfSuggestionsInsideNetwork]);

    const numberOfSuggestionsOutsideNetwork = useMemo(() => {
        return contactSuggestions.filter(
            (suggestion) => suggestion.reason === ContactSuggestionReason.SIMILAR_TO_OTHER_ACCOUNTS.toString()
        ).length;
    }, [contactSuggestions]);
    const hasSuggestionsOutsideNetwork = useMemo(() => {
        return numberOfSuggestionsOutsideNetwork > 0;
    }, [numberOfSuggestionsOutsideNetwork]);

    const isInsideNetworkSelected = searchParams.get('insideNetwork') !== 'false';

    useEffect(() => {
        if (!location.search.includes('insideNetwork')) {
            setSearchParams((prevParams) => {
                const newParams = new URLSearchParams(prevParams);
                if (hasSuggestionsInsideNetwork) {
                    newParams.set('insideNetwork', 'true');
                } else if (hasSuggestionsOutsideNetwork) {
                    newParams.set('insideNetwork', 'false');
                }
                return newParams;
            });
        }
    }, [hasSuggestionsInsideNetwork, hasSuggestionsOutsideNetwork, setSearchParams, location.pathname]);

    // Add this check to force 'false' when there are no inside network suggestions
    useEffect(() => {
        if (!hasSuggestionsInsideNetwork && hasSuggestionsOutsideNetwork) {
            setSearchParams((prevParams) => {
                const newParams = new URLSearchParams(prevParams);
                newParams.set('insideNetwork', 'false');
                return newParams;
            });
        }
        if (!hasSuggestionsOutsideNetwork && hasSuggestionsInsideNetwork) {
            setSearchParams((prevParams) => {
                const newParams = new URLSearchParams(prevParams);
                newParams.set('insideNetwork', 'true');
                return newParams;
            });
        }
    }, [hasSuggestionsInsideNetwork, hasSuggestionsOutsideNetwork, setSearchParams]);

    if (!hasSuggestionsInsideNetwork && !hasSuggestionsOutsideNetwork) return null;

    const handleInsideClick = () => {
        if (!hasSuggestionsInsideNetwork) return;
        setSearchParams((prevParams) => {
            prevParams.set('insideNetwork', 'true');
            return prevParams;
        });
    };

    const handleOutsideClick = () => {
        if (!hasSuggestionsOutsideNetwork) return;
        setSearchParams((prevParams) => {
            prevParams.set('insideNetwork', 'false');
            return prevParams;
        });
    };

    return (
        <TooltipProvider>
            <div className={cn('flex items-center gap-1', !shadcnSearchEnabled && 'hidden')}>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <button
                            onClick={handleInsideClick}
                            disabled={!hasSuggestionsInsideNetwork}
                            className={cn(
                                'flex items-center gap-2 px-3 py-2 rounded text-sm font-medium transition-all',
                                isInsideNetworkSelected && hasSuggestionsInsideNetwork
                                    ? 'bg-background-tertiary text-foreground shadow-none'
                                    : 'text-muted-foreground hover:text-foreground hover:bg-muted',
                                !hasSuggestionsInsideNetwork && 'opacity-50 cursor-not-allowed'
                            )}
                        >
                            <span>Inside network</span>
                            <span
                                className={cn(
                                    'min-w-[20px] h-5 px-1.5 rounded-full text-xs font-medium flex items-center justify-center',
                                    isInsideNetworkSelected && hasSuggestionsInsideNetwork
                                        ? 'bg-background text-muted-foreground'
                                        : 'bg-muted text-muted-foreground'
                                )}
                            >
                                {numberOfSuggestionsInsideNetwork}
                            </span>
                        </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">Contacts who are already in your LinkedIn network</TooltipContent>
                </Tooltip>

                <Tooltip>
                    <TooltipTrigger asChild>
                        <button
                            onClick={handleOutsideClick}
                            disabled={!hasSuggestionsOutsideNetwork}
                            className={cn(
                                'flex items-center gap-2 px-3 py-2 rounded text-sm font-medium transition-all',
                                !isInsideNetworkSelected && hasSuggestionsOutsideNetwork
                                    ? 'bg-background-tertiary text-foreground shadow-none'
                                    : 'text-muted-foreground hover:text-foreground hover:bg-muted',
                                !hasSuggestionsOutsideNetwork && 'opacity-50 cursor-not-allowed'
                            )}
                        >
                            <span>Outside network</span>
                            <span
                                className={cn(
                                    'min-w-[20px] h-5 px-1.5 rounded-full text-xs font-medium flex items-center justify-center',
                                    !isInsideNetworkSelected && hasSuggestionsOutsideNetwork
                                        ? 'bg-background text-muted-foreground'
                                        : 'bg-muted text-muted-foreground'
                                )}
                            >
                                {numberOfSuggestionsOutsideNetwork}
                            </span>
                        </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                        Contacts who are not yet in your LinkedIn network but may be relevant
                    </TooltipContent>
                </Tooltip>
            </div>
        </TooltipProvider>
    );
};
